/* ============================================================
   RAW & HUNGRY — Interaction engine
   ============================================================ */
/* ------------------------------------------------------------
   The original site drove all motion through Barba.js page
   transitions. Next.js owns routing here, so Barba is removed and
   its lifecycle hooks are replaced 1:1:

     Barba `once` / `afterEnter`  ->  initPage(container)
     Barba `afterLeave`           ->  cleanupPage(container)
     Barba `beforeEnter`          ->  preparePage(container)
     Barba pixel-wipe transition  ->  ./transition.ts (Next router)

   Every animation algorithm (word split, scrub, horizontal pin,
   splatter, cursor, custom scrollbar, footer weather, magnetic
   buttons, reveals, parallax) is preserved verbatim so the runtime
   behaviour is identical. GSAP / Lenis are now real npm imports
   instead of CDN globals (same pinned versions: gsap 3.12.5,
   lenis 1.1.13).
   ============================================================ */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';
import Lenis from 'lenis';

const DIM = 0.14; // resting opacity of un-revealed words

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function canHover(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
}
function usesTouchPrimaryInput(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(hover: none), (pointer: coarse)').matches ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  );
}

let stableViewportHeight = 0;

function getStableViewportHeight(): number {
  return stableViewportHeight || window.innerHeight;
}

/* Keep all viewport-height sections stable while mobile browser chrome expands/collapses.
   Safari reports those toolbar animations as height-only resizes; updating only
   when the layout width changes still handles rotation and split-view changes. */
function setupViewportResizeHandling(): void {
  const root = document.documentElement;
  const lockMobileViewportHeight = usesTouchPrimaryInput();
  let layoutWidth = root.clientWidth;

  const lockViewportHeight = () => {
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    stableViewportHeight = Math.round(viewportHeight);
    root.style.setProperty('--rh-stable-viewport-height', `${stableViewportHeight}px`);
  };

  if (lockMobileViewportHeight) lockViewportHeight();

  window.addEventListener(
    'resize',
    () => {
      const nextWidth = root.clientWidth;
      const widthChanged = Math.abs(nextWidth - layoutWidth) >= 2;

      // Ignore Safari's address-bar animation and the mobile keyboard. Neither
      // changes layout width, so neither should invalidate scroll measurements.
      if (lockMobileViewportHeight && !widthChanged) return;

      layoutWidth = nextWidth;
      if (lockMobileViewportHeight) lockViewportHeight();
      triggerFieldRectsUpdate();
      refreshDocHeight();
    },
    { passive: true },
  );
}

/* ---------------- Module state ---------------- */
let lenis: Lenis | null = null;
let onceInitialised = false;
let updateScrollbar: () => void = () => {};
let pageContext: gsap.Context | null = null;
let pageContextContainer: HTMLElement | null = null;

type SplatDot = {
  el: HTMLElement;
  depth: number;
  baseOp: number;
  phase: number;
  amp: number;
  ox: number;
  oy: number;
  currX: number;
  currY: number;
  currGlow: number;
};
type CachedRect = { top: number; bottom: number; left: number; width: number; height: number };
function toCachedRect(r: DOMRect): CachedRect {
  return { top: r.top, bottom: r.bottom, left: r.left, width: r.width, height: r.height };
}

type SplatEntry = { container: HTMLElement; dots: SplatDot[]; rect: CachedRect | null };
const splatField: SplatEntry[] = [];

type OrbitRing = {
  el: HTMLElement;
  depth: number;
  baseOp: number;
  targetOp: number;
  phase: number;
  size: number;
  scaleFactor: number;
  entranceScale: number;
  currX: number;
  currY: number;
};
type OrbitEntry = { container: HTMLElement; rings: OrbitRing[]; rect: CachedRect | null; scrubDriven: boolean };
const orbitField: OrbitEntry[] = [];

let fieldRectsDirty = true;
let fieldRectsScrollY = 0;
export function triggerFieldRectsUpdate(): void {
  fieldRectsDirty = true;
}

const initializedContainers = new WeakSet<HTMLElement>();

declare global {
  interface Window {
    __sv?: number;
    RH_THEME?: {
      weatherUrl?: string;
      locale?: string;
      homeUrl?: string;
      adminBar?: boolean;
    };
  }
}

export function getLenis(): Lenis | null {
  return lenis;
}
export function refreshScrollTrigger(): void {
  if (typeof ScrollTrigger !== 'undefined') {
    ScrollTrigger.refresh(true);
  }
  fieldRectsDirty = true; // layout may have shifted — re-measure splat/orbit rects
}
export function lenisStop(): void {
  if (lenis) lenis.stop();
}
export function lenisStart(): void {
  if (lenis) lenis.start();
}

/* ---------------- Smooth scroll (Lenis) ---------------- */
function initLenis(): void {
  // Touch devices already have native momentum scrolling. Keeping Lenis on
  // their GSAP ticker adds a permanent frame loop without improving touch
  // input and increases pressure on mobile Safari during pinned sections.
  if (lenis || prefersReducedMotion() || usesTouchPrimaryInput()) return;
  lenis = new Lenis({
    duration: 1.15,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });

  // Lenis is driven exclusively by the GSAP ticker below — a second
  // requestAnimationFrame loop would run the scroll lerp twice per frame.
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((t) => {
    if (lenis) lenis.raf(t * 1000);
  });
  gsap.ticker.lagSmoothing(0);
}

/* ---------------- Word splitter ---------------- */
function splitIntoWords(el: HTMLElement): HTMLElement[] {
  const words: HTMLElement[] = [];
  function walk(node: Node, target: Node) {
    if (node.nodeType === 3) {
      const parts = (node.textContent || '').split(/(\s+)/);
      parts.forEach((p) => {
        if (p === '') return;
        if (/^\s+$/.test(p)) {
          target.appendChild(document.createTextNode(p));
          return;
        }
        const w = document.createElement('span');
        w.className = 'word';
        w.textContent = p;
        target.appendChild(w);
        words.push(w);
      });
    } else if (node.nodeType === 1) {
      const shell = (node as HTMLElement).cloneNode(false);
      target.appendChild(shell);
      Array.from(node.childNodes).forEach((c) => walk(c, shell));
    }
  }
  const kids = Array.from(el.childNodes);
  el.textContent = '';
  kids.forEach((k) => walk(k, el));
  return words;
}

function groupWords(words: HTMLElement[]): HTMLElement[][] {
  const groups: HTMLElement[][] = [];
  if (!words.length) return groups;
  groups.push([words[0]]);
  for (let i = 1; i < words.length; i += 2) groups.push(words.slice(i, i + 2));
  return groups;
}

function dim(words: HTMLElement[]): void {
  // Subtle scale for the hidden state. Deliberately NO filter: blur — a
  // per-word blur forces dozens of re-rasterizing layers during reveals
  // (opacity/transform stay on the compositor, filter does not).
  gsap.set(words, {
    autoAlpha: DIM,
    yPercent: 0,
    scale: 0.97,
    transformOrigin: '50% 50%'
  });
}

/* ---------------- Word reveals ---------------- */
function setupWordReveals(container: HTMLElement): void {
  const wordsToReveal: HTMLElement[] = [];
  const wordsToScrub: { el: HTMLElement; groups: HTMLElement[][] }[] = [];

  container.querySelectorAll<HTMLElement>('[data-words]').forEach((el) => {
    if (el.closest('[data-scrub]')) return;

    let words: HTMLElement[];
    if (el.dataset.splitDone === 'true') {
      words = Array.from(el.querySelectorAll<HTMLElement>('.word'));
    } else {
      words = splitIntoWords(el);
      el.dataset.splitDone = 'true';
      dim(words);
    }

    const groups = groupWords(words);
    if (prefersReducedMotion()) return;

    const isHero = el.closest('.hero');
    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const elementDocTop = rect.top + scrollTop;

    // Direct reveal for hero or elements already in viewport
    const isInViewport =
      (rect.top < getStableViewportHeight() * 0.9 && rect.bottom > 0) ||
      elementDocTop < getStableViewportHeight();

    if (isInViewport || isHero) {
      const tl = gsap.timeline();
      groups.forEach((g, i) => {
        tl.to(g, {
          autoAlpha: 1,
          yPercent: 0,
          scale: 1,
          duration: 0.55,
          ease: 'osmo'
        }, i * 0.08);
      });
    } else {
      // Collect for batching (discrete) or individual scrub
      // For now, if it's not scrubbed, we'll batch the reveal
      groups.forEach((g) => {
        wordsToReveal.push(...g);
      });
    }
  });

  // Enterprise Batching: Handles multiple lines/words entering viewport with a single coordinated stagger
  if (wordsToReveal.length > 0) {
    ScrollTrigger.batch(wordsToReveal, {
      start: 'top 88%',
      onEnter: (batch) => {
        gsap.to(batch, {
          autoAlpha: 1,
          yPercent: 0,
          scale: 1,
          duration: 0.5,
          ease: 'osmo',
          stagger: 0.04,
          overwrite: 'auto',
        });
      },
      once: true,
    });
  }
}

/* ---------------- Transition Hooks & Views ---------------- */
// Faithful to the Barba philosophy: allow for page-specific lifecycle logic.
type View = {
  namespace: string;
  beforeEnter?: (container: HTMLElement) => void;
  afterEnter?: (container: HTMLElement) => void;
  beforeLeave?: (container: HTMLElement) => void;
};
const registeredViews: View[] = [];

export function registerView(view: View) {
  registeredViews.push(view);
}

type BackstoryStat = {
  value: number;
  rawValue: string;
  suffix: string;
  label: string;
};

function rhElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

type NetworkStoryChapter = {
  titleA: string;
  titleB: string;
  copy: string;
  capabilities: string[];
  metric?: string;
  metricLabel?: string;
};

function enhanceNetworkSections(container: HTMLElement): void {
  if (
    document.body.classList.contains('elementor-editor-active') ||
    document.body.classList.contains('elementor-editor-preview')
  ) {
    return;
  }

  const sections = new Set<HTMLElement>();
  container.querySelectorAll<HTMLElement>('[data-rh-network-story]').forEach((section) => sections.add(section));
  container.querySelectorAll<HTMLElement>('.network-grid, .cases-grid').forEach((grid) => {
    const section = grid.closest<HTMLElement>('section');
    if (section) sections.add(section);
  });

  sections.forEach((section) => {
    if (section.dataset.rhNetworkReady === 'true') return;

    const eyebrowText = section.querySelector<HTMLElement>('.eyebrow')?.textContent || '';
    const isExplicit = section.hasAttribute('data-rh-network-story');
    const isNetwork = /\bnetzwerk\b|\bnetwork\b/i.test(eyebrowText);
    if (!isExplicit && !isNetwork) return;

    const isEnglish = window.RH_THEME?.locale === 'en' || /\bnetwork\b/i.test(eyebrowText);
    const copy: {
      eyebrow: string;
      titleA: string;
      titleB: string;
      intro: string;
      visualLabel: string;
      chapters: NetworkStoryChapter[];
    } = isEnglish
      ? {
          eyebrow: 'Network',
          titleA: 'Part of a strong',
          titleB: 'network.',
          intro:
            'RAW&HUNGRY connects digital products, technology and performance with the strategic, creative and communications strength of an established agency group.',
          visualLabel: 'Connected expertise across digital, communication, design and technology',
          chapters: [
            {
              titleA: 'campaigners',
              titleB: 'Network',
              copy:
                "As a digital agency, we are part of campaigners Network. Together with campaigners for strategic and political communication, not everybody's darling for design and branding, and schiller+faust for PR, we combine digital products, communication, brand management and public relations in one connected offering.",
              capabilities: ['Digital products', 'Strategic communication', 'Branding', 'Public relations'],
            },
            {
              titleA: 'Meyle+Müller',
              titleB: 'Group',
              copy:
                'The Meyle+Müller Group is one of Germany\'s leading media and IT service providers and has invested in innovative technologies for more than 100 years. This gives us access to specialists and production expertise ranging from AI-powered workflows to 3D visualisation, enabling tailored cross-media campaigns from a single source.',
              capabilities: ['AI workflows', '3D visualisation', 'Cross-media', 'Media & IT'],
              metric: '100+',
              metricLabel: 'years of technology experience',
            },
          ],
        }
      : {
          eyebrow: 'Netzwerk',
          titleA: 'Teil eines starken',
          titleB: 'Netzwerks.',
          intro:
            'RAW&HUNGRY verbindet digitale Produkte, Technologie und Performance mit der strategischen, kreativen und kommunikativen Kraft eines eingespielten Agenturverbunds.',
          visualLabel: 'Vernetzte Kompetenz aus Digital, Kommunikation, Design und Technologie',
          chapters: [
            {
              titleA: 'campaigners',
              titleB: 'Network',
              copy:
                "Als Digitalagentur sind wir Teil des campaigners Network. Gemeinsam mit campaigners für strategische und politische Kommunikation, not everybody's darling für Design und Branding sowie schiller+faust für PR bündeln wir digitale Produkte, Kommunikation, Markenführung und Public Relations. So entsteht eine vernetzte Leistung aus einer Hand.",
              capabilities: ['Digitale Produkte', 'Strategische Kommunikation', 'Branding', 'Public Relations'],
            },
            {
              titleA: 'Meyle+Müller',
              titleB: 'Gruppe',
              copy:
                'Die Meyle+Müller-Gruppe zählt zu den führenden Medien- und IT-Dienstleistern Deutschlands und investiert seit mehr als 100 Jahren in innovative Technologien. Das gibt uns Zugriff auf Spezialisten und Produktionskompetenz von KI-gestützten Workflows bis zu 3D-Visualisierungen. Gemeinsam realisieren wir maßgeschneiderte, crossmediale Kampagnen aus einer Hand.',
              capabilities: ['KI-Workflows', '3D-Visualisierung', 'Crossmedia', 'Media & IT'],
              metric: '100+',
              metricLabel: 'Jahre Technologie-Erfahrung',
            },
          ],
        };

    const wrap = rhElement('div', 'wrap rh-network-story__wrap');
    const header = rhElement('header', 'rh-network-story__header');
    header.dataset.reveal = 'true';
    const eyebrow = rhElement('p', 'eyebrow rh-network-story__eyebrow');
    eyebrow.append(
      rhElement('span', 'idx', '(02)'),
      document.createTextNode(` ${copy.eyebrow} `),
      rhElement('span', 'ln'),
    );
    const title = rhElement('h2', 'h-lg rh-network-story__title');
    title.append(
      rhElement('span', 'rh-network-story__title-line', `${copy.titleA} `),
      rhElement('span', 'rh-network-story__title-line serif-it', copy.titleB),
    );
    header.append(eyebrow, title, rhElement('p', 'rh-network-story__intro', copy.intro));

    const visual = rhElement('figure', 'rh-network-story__visual');
    visual.dataset.reveal = 'true';
    visual.setAttribute('aria-label', copy.visualLabel);
    visual.setAttribute('role', 'img');
    const visualMedia = rhElement('div', 'rh-network-story__visual-media');
    visualMedia.dataset.parallax = 'true';
    visual.append(visualMedia);

    const chapters = rhElement('div', 'rh-network-story__chapters');
    chapters.dataset.revealStagger = 'true';
    copy.chapters.forEach((chapter, index) => {
      const article = rhElement(
        'article',
        `rh-network-story__chapter${index === 1 ? ' rh-network-story__chapter--group' : ''}`,
      );
      const identity = rhElement('div', 'rh-network-story__identity');
      const chapterTitle = rhElement('h3', 'rh-network-story__chapter-title');
      chapterTitle.append(
        rhElement('span', '', `${chapter.titleA} `),
        rhElement('span', 'serif-it', chapter.titleB),
      );
      identity.append(chapterTitle);
      if (chapter.metric && chapter.metricLabel) {
        const metric = rhElement('p', 'rh-network-story__metric');
        metric.append(
          rhElement('strong', '', chapter.metric),
          rhElement('span', '', chapter.metricLabel),
        );
        identity.append(metric);
      }

      const body = rhElement('div', 'rh-network-story__body');
      body.append(rhElement('p', 'prose rh-network-story__copy', chapter.copy));
      const capabilities = rhElement('ul', 'rh-network-story__capabilities');
      chapter.capabilities.forEach((capability) => {
        capabilities.append(rhElement('li', '', capability));
      });
      body.append(capabilities);
      article.append(identity, body);
      chapters.append(article);
    });

    wrap.append(header, visual, chapters);
    section.replaceChildren(wrap);
    section.classList.add('rh-network-story');
    section.dataset.rhNetworkStory = 'true';
    section.dataset.rhNetworkReady = 'true';
  });
}

function enhanceBackstorySections(container: HTMLElement): void {
  if (
    document.body.classList.contains('elementor-editor-active') ||
    document.body.classList.contains('elementor-editor-preview')
  ) {
    return;
  }

  const sections = new Set<HTMLElement>();
  container.querySelectorAll<HTMLElement>('[data-rh-backstory]').forEach((section) => sections.add(section));
  container.querySelectorAll<HTMLElement>('.stats').forEach((stats) => {
    const section = stats.closest<HTMLElement>('section');
    if (section) sections.add(section);
  });

  sections.forEach((section) => {
    if (section.dataset.rhBackstoryReady === 'true') return;

    const eyebrowText = section.querySelector<HTMLElement>('.eyebrow')?.textContent || '';
    const isExplicit = section.hasAttribute('data-rh-backstory');
    const isBackstory = /im\s+r[uü]cken|behind\s+us/i.test(eyebrowText);
    if (!isExplicit && !isBackstory) return;

    const statNodes = Array.from(section.querySelectorAll<HTMLElement>('.stats > .stat')).slice(0, 3);
    if (statNodes.length !== 3) return;

    const stats: BackstoryStat[] = statNodes.map((stat) => {
      const rawValue = (stat.querySelector<HTMLElement>('.num')?.textContent || '0').trim();
      const numeric = rawValue.match(/\d+/)?.[0] || '0';
      return {
        value: Number.parseInt(numeric, 10),
        rawValue,
        suffix: rawValue.replace(numeric, '').trim(),
        label: (stat.querySelector<HTMLElement>('.lbl')?.textContent || '').trim(),
      };
    });

    const isEnglish = /behind\s+us/i.test(eyebrowText);
    const copy = isEnglish
      ? {
          kicker: 'Meyle+Müller × campaigners Network',
          titleA: 'Behind',
          titleB: 'us',
          titleLabel: 'Behind us',
          intro: 'Three figures. One network that turns experience into forward momentum.',
          stepTitles: ['Experience', 'Network', 'Places'],
        }
      : {
          kicker: 'Meyle+Müller × campaigners Network',
          titleA: 'Im',
          titleB: 'Rücken',
          titleLabel: 'Im Rücken',
          intro: 'Drei Zahlen. Ein Netzwerk, das Erfahrung in Vorwärtsdrang übersetzt.',
          stepTitles: ['Erfahrung', 'Verbund', 'Orte'],
        };

    const stage = rhElement('div', 'wrap rh-backstory__stage');
    const header = rhElement('header', 'rh-backstory__header');
    const kicker = rhElement('p', 'rh-backstory__kicker', copy.kicker);
    const title = rhElement('h2', 'rh-backstory__title');
    title.setAttribute('aria-label', copy.titleLabel);
    const titleA = rhElement('span', 'rh-backstory__title-line', copy.titleA);
    titleA.dataset.rhBackstoryTitle = '';
    const titleB = rhElement('span', 'rh-backstory__title-line', copy.titleB);
    titleB.dataset.rhBackstoryTitle = '';
    title.append(titleA, titleB);
    const intro = rhElement('p', 'rh-backstory__intro', copy.intro);
    header.append(kicker, title, intro);

    const metricShell = rhElement('div', 'rh-backstory__metric-shell');
    const metricCore = rhElement('div', 'rh-backstory__metric-core');
    const metricMeta = rhElement('div', 'rh-backstory__metric-meta');
    const activeTitle = rhElement('span', 'rh-backstory__active-title', copy.stepTitles[0]);
    activeTitle.dataset.rhBackstoryActiveTitle = '';
    const activeIndex = rhElement('span', 'rh-backstory__active-index', '01 / 03');
    activeIndex.dataset.rhBackstoryActiveIndex = '';
    metricMeta.append(activeTitle, activeIndex);

    const counter = rhElement('div', 'rh-backstory__counter');
    counter.setAttribute('aria-hidden', 'true');
    const counterValue = rhElement('span', 'rh-backstory__counter-value', '0');
    counterValue.dataset.rhBackstoryCount = '';
    const counterSuffix = rhElement('span', 'rh-backstory__counter-suffix', stats[0].suffix);
    counterSuffix.dataset.rhBackstorySuffix = '';
    counter.append(counterValue, counterSuffix);

    const activeCopy = rhElement('p', 'rh-backstory__active-copy', stats[0].label);
    activeCopy.dataset.rhBackstoryActiveCopy = '';
    metricCore.append(metricMeta, counter, activeCopy);
    metricShell.append(metricCore);

    const guide = rhElement('div', 'rh-backstory__guide');
    guide.setAttribute('aria-hidden', 'true');
    const route = rhElement('div', 'rh-backstory__route');
    const routeProgress = rhElement('i', 'rh-backstory__route-progress');
    route.append(routeProgress);
    guide.append(route);

    const steps = rhElement('ol', 'rh-backstory__steps');
    steps.setAttribute('aria-label', copy.titleLabel);
    stats.forEach((stat, index) => {
      const step = rhElement('li', `rh-backstory__step${index === 0 ? ' is-active' : ''}`);
      step.dataset.rhBackstoryTarget = String(stat.value);
      step.dataset.rhBackstorySuffix = stat.suffix;
      const top = rhElement('div', 'rh-backstory__step-top');
      top.append(
        rhElement('span', 'rh-backstory__step-title', copy.stepTitles[index]),
        rhElement('span', 'rh-backstory__step-value', stat.rawValue),
      );
      step.append(top, rhElement('p', 'rh-backstory__step-copy', stat.label));
      steps.append(step);
    });

    stage.append(header, metricShell, guide, steps);
    section.replaceChildren(stage);
    section.classList.add('rh-backstory');
    section.dataset.rhBackstoryReady = 'true';
  });
}

function setupBackstory(section: HTMLElement): void {
  if (section.dataset.rhBackstoryReady !== 'true') return;

  const title = section.querySelector<HTMLElement>('.rh-backstory__title');
  const titleLines = section.querySelectorAll<HTMLElement>('[data-rh-backstory-title]');
  const kicker = section.querySelector<HTMLElement>('.rh-backstory__kicker');
  const intro = section.querySelector<HTMLElement>('.rh-backstory__intro');
  const metricShell = section.querySelector<HTMLElement>('.rh-backstory__metric-shell');
  const counter = section.querySelector<HTMLElement>('.rh-backstory__counter');
  const count = section.querySelector<HTMLElement>('[data-rh-backstory-count]');
  const suffix = section.querySelector<HTMLElement>('[data-rh-backstory-suffix]');
  const activeTitle = section.querySelector<HTMLElement>('[data-rh-backstory-active-title]');
  const activeCopy = section.querySelector<HTMLElement>('[data-rh-backstory-active-copy]');
  const activeIndex = section.querySelector<HTMLElement>('[data-rh-backstory-active-index]');
  const route = section.querySelector<HTMLElement>('.rh-backstory__route');
  const routeProgress = section.querySelector<HTMLElement>('.rh-backstory__route-progress');
  const steps = Array.from(section.querySelectorAll<HTMLElement>('.rh-backstory__step'));
  if (
    !title ||
    !titleLines.length ||
    !kicker ||
    !intro ||
    !metricShell ||
    !counter ||
    !count ||
    !suffix ||
    !activeTitle ||
    !activeCopy ||
    !activeIndex ||
    !route ||
    !routeProgress ||
    steps.length !== 3
  ) {
    return;
  }

  const targets = steps.map((step) => Number.parseInt(step.dataset.rhBackstoryTarget || '0', 10));
  let activeStep = -1;
  let lastCounter = -1;
  let lastSuffix = '';

  const setStep = (index: number) => {
    if (index === activeStep) return;
    const previousStep = activeStep;
    activeStep = index;
    steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === index));
    activeTitle.textContent = steps[index].querySelector<HTMLElement>('.rh-backstory__step-title')?.textContent || '';
    activeCopy.textContent = steps[index].querySelector<HTMLElement>('.rh-backstory__step-copy')?.textContent || '';
    activeIndex.textContent = `${String(index + 1).padStart(2, '0')} / 03`;
    if (previousStep >= 0) {
      gsap.fromTo(
        [activeTitle, activeCopy],
        { y: 12, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.52, stagger: 0.06, ease: 'osmo', overwrite: true },
      );
      gsap.fromTo(
        counter,
        { scale: 0.96, autoAlpha: 0.56 },
        { scale: 1, autoAlpha: 1, duration: 0.62, ease: 'osmo', overwrite: true },
      );
    }
  };

  const renderCounter = (index: number, localProgress: number) => {
    const reveal = gsap.utils.clamp(0, 1, (localProgress - 0.06) / 0.56);
    const eased = 1 - Math.pow(1 - reveal, 3);
    const nextCounter = Math.round(targets[index] * eased);
    const nextSuffix = steps[index].dataset.rhBackstorySuffix || '';
    if (nextCounter !== lastCounter) {
      count.textContent = String(nextCounter);
      lastCounter = nextCounter;
    }
    if (nextSuffix !== lastSuffix) {
      suffix.textContent = nextSuffix;
      lastSuffix = nextSuffix;
    }
  };

  if (prefersReducedMotion() || window.innerWidth < 900) {
    section.classList.add('is-static');
    steps.forEach((step) => gsap.set(step, { autoAlpha: 1, y: 0, scale: 1 }));
    setStep(0);
    renderCounter(0, 1);
    return;
  }

  gsap.set(section, { zIndex: 6 });
  gsap.set(steps, { autoAlpha: 0.42, y: 0 });
  gsap.set(routeProgress, { scaleX: 0, transformOrigin: 'left center' });

  const timeline = gsap.timeline({
    defaults: { ease: 'osmo' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => '+=' + Math.round(Math.max(getStableViewportHeight() * 3.25, 2800)),
      pin: true,
      pinSpacing: true,
      scrub: 0.75,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onToggle: (self) => {
        section.classList.toggle('is-active', self.isActive);
        gsap.set(section, { zIndex: self.isActive ? 10 : 6 });
      },
      onUpdate: (self) => {
        gsap.set(routeProgress, { scaleX: self.progress });
        const statsProgress = gsap.utils.clamp(0, 0.9999, (self.progress - 0.14) / 0.72);
        const scaled = statsProgress * 3;
        const index = self.progress >= 0.86 ? 2 : Math.min(2, Math.floor(scaled));
        const localProgress = self.progress >= 0.86 ? 1 : scaled - index;
        setStep(index);
        renderCounter(index, localProgress);
      },
    },
  });

  timeline
    .addLabel('title', 0)
    .fromTo(kicker, { y: 26, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.72 }, 'title')
    .fromTo(
      titleLines,
      { yPercent: 108, autoAlpha: 0 },
      { yPercent: 0, autoAlpha: 1, duration: 1.02, stagger: 0.08 },
      'title+=0.1',
    )
    .fromTo(intro, { y: 28, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.78 }, 'title+=0.42')
    .fromTo(
      metricShell,
      { y: 42, scale: 0.975, autoAlpha: 0 },
      { y: 0, scale: 1, autoAlpha: 1, duration: 1.08 },
      'title+=0.46',
    )
    .fromTo(route, { y: 12, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.74 }, 'title+=0.72')
    .fromTo(steps, { y: 18, autoAlpha: 0 }, { y: 0, autoAlpha: 0.42, duration: 0.72, stagger: 0.06 }, 'title+=0.82')
    .to({}, { duration: 3.4 });

  setStep(0);
  renderCounter(0, 0);
}

// Pre-split and dim elements on the incoming page to avoid FOUC before reveal.
export function preparePage(container: HTMLElement): void {
  enhanceNetworkSections(container);
  enhanceBackstorySections(container);
  container.querySelectorAll<HTMLElement>('[data-words]').forEach((el) => {
    if (el.dataset.splitDone === 'true') return;
    const words = splitIntoWords(el);
    el.dataset.splitDone = 'true';
    dim(words);
  });
  buildSplatter(container);
}

/* ---------------- Pinned Word Scrub ---------------- */
function setupScrub(sec: HTMLElement): void {
  const blocks = sec.querySelectorAll<HTMLElement>('[data-words]');
  const inner = sec.querySelector<HTMLElement>('.scrub-inner');
  if (!inner) return;

  let groups: HTMLElement[][] = [];
  blocks.forEach((b) => {
    let w: HTMLElement[];
    if (b.dataset.splitDone === 'true') {
      w = Array.from(b.querySelectorAll<HTMLElement>('.word'));
    } else {
      w = splitIntoWords(b);
      b.dataset.splitDone = 'true';
      dim(w);
    }
    groups = groups.concat(groupWords(w));
  });
  if (!groups.length) return;

  const contentH = inner.offsetHeight;
  const pinLen = Math.round(Math.max(getStableViewportHeight(), contentH) + groups.length * 45);

  // Default `fixed` pinning (Lenis scrolls natively, so a position:fixed pin
  // is compositor-stable): `pinType: 'transform'` repositioned the section per
  // JS update and visibly shimmered against the composited scroll at slow/fast
  // wheel speeds. z-index flips only at the exact pin boundaries (onToggle),
  // where the section sits at its natural layout position — flipping mid-pin
  // at progress 0.05/0.95 popped the section above/behind overlapping
  // neighbours while fully visible.
  gsap.set(sec, { zIndex: 5 });
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sec,
      start: 'top top',
      end: '+=' + pinLen,
      pin: true,
      scrub: 0.5,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onToggle: (self) => {
        gsap.set(sec, { zIndex: self.isActive ? 10 : 5 });
      },
    },
  });

  const per = 1;
  const total = groups.length * per;

  // Keep the CTA's rings on this same pinned timeline. Their former
  // viewport-position calculation changed as soon as the section pinned,
  // producing a visible jump that the quote wall never had.
  const scrubOrbit = sec.querySelector<HTMLElement>('[data-scrub-orbit]');
  const orbitEntry = scrubOrbit ? orbitField.find((entry) => entry.container === scrubOrbit) : undefined;
  if (orbitEntry?.scrubDriven) {
    tl.to(
      orbitEntry.rings,
      {
        baseOp: (i: number) => orbitEntry.rings[i].targetOp,
        entranceScale: 1,
        duration: Math.max(1, total * 0.45),
        ease: 'power1.inOut',
        stagger: 0.035,
      },
      0
    );
  }

  groups.forEach((g, i) => {
    tl.to(
      g,
      { autoAlpha: 1, yPercent: 0, ease: 'power1.inOut', duration: per },
      i * (per * 0.85)
    );
  });

  tl.fromTo(
    inner,
    {
      y: () => {
        const vh = getStableViewportHeight();
        const h = inner.offsetHeight;
        const s = window.getComputedStyle(sec);
        const pt = parseFloat(s.paddingTop) || 0;
        const pb = parseFloat(s.paddingBottom) || 0;
        const ch = h + pt + pb;
        return ch > vh ? (ch - vh) * 0.5 : 0;
      },
    },
    {
      y: () => {
        const vh = getStableViewportHeight();
        const h = inner.offsetHeight;
        const s = window.getComputedStyle(sec);
        const pt = parseFloat(s.paddingTop) || 0;
        const pb = parseFloat(s.paddingBottom) || 0;
        const ch = h + pt + pb;
        return ch > vh ? -(ch - vh) * 0.5 : 0;
      },
      ease: 'none',
      duration: total,
    },
    0
  );

  tl.to({}, { duration: total * 0.15 });
}

/* ---------------- Horizontal Pinned Process ---------------- */
function setupHorizontalOne(sec: HTMLElement): void {
  const track = sec.querySelector<HTMLElement>('.h-track');
  const bar = sec.querySelector<HTMLElement>('.h-progress > i');
  if (!track) return;
  const getDist = () => Math.max(0, track.scrollWidth - window.innerWidth);
  gsap.to(track, {
    x: () => -getDist(),
    ease: 'none',
    scrollTrigger: {
      trigger: sec,
      start: 'top top',
      end: () => '+=' + getDist(),
      pin: true,
      scrub: 0.6,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        // scaleX (compositor-only) instead of width (layout per frame).
        if (bar) bar.style.transform = 'scaleX(' + self.progress + ')';
      },
    },
  });
}

/* ---------------- Generic reveals + parallax ---------------- */
// Reduced-motion reveal observers, tracked per container so cleanupPage can
// disconnect them instead of leaving them to the GC across navigations.
const revealObservers: { container: HTMLElement; io: IntersectionObserver }[] = [];

type MarqueeEntry = {
  container: HTMLElement;
  marquee: HTMLElement;
  track: HTMLElement;
  clones: HTMLElement[];
  loop: gsap.core.Tween;
  observer: IntersectionObserver;
  velocityTrigger: ScrollTrigger | null;
  speedReset: gsap.core.Tween | null;
};

const marqueeEntries: MarqueeEntry[] = [];

function teardownMarquee(entry: MarqueeEntry): void {
  entry.observer.disconnect();
  entry.velocityTrigger?.kill();
  entry.speedReset?.kill();
  entry.loop.kill();
  entry.clones.forEach((clone) => clone.remove());
  entry.track.style.removeProperty('transform');
  entry.track.style.removeProperty('translate');
  delete entry.track.dataset.rhMarqueeLoop;
}

function cleanupMarquees(container: HTMLElement | null): void {
  for (let index = marqueeEntries.length - 1; index >= 0; index--) {
    const entry = marqueeEntries[index];
    if (container && !container.contains(entry.marquee)) continue;
    teardownMarquee(entry);
    marqueeEntries.splice(index, 1);
  }
}

function setupMarquees(container: HTMLElement): void {
  if (prefersReducedMotion()) return;

  container.querySelectorAll<HTMLElement>('.marquee').forEach((marquee) => {
    const track = marquee.querySelector<HTMLElement>('.marquee-track');
    if (!track || track.dataset.rhMarqueeLoop === 'true') return;

    const originals = Array.from(track.children).filter(
      (element): element is HTMLElement => element instanceof HTMLElement,
    );
    if (!originals.length) return;

    const clones = originals.map((original) => {
      const clone = original.cloneNode(true) as HTMLElement;
      clone.dataset.rhMarqueeClone = '';
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    });
    track.append(...clones);
    track.dataset.rhMarqueeLoop = 'true';

    const oneCycleWidth = track.scrollWidth / 2;
    const loop = gsap.fromTo(
      track,
      { xPercent: 0 },
      {
        xPercent: -50,
        duration: Math.max(18, oneCycleWidth / 85),
        repeat: -1,
        ease: 'none',
      },
    );

    const observer = new IntersectionObserver(
      ([visibility]) => loop.paused(!visibility?.isIntersecting),
      { rootMargin: '160px 0px' },
    );
    observer.observe(marquee);

    const entry = {
      container,
      marquee,
      track,
      clones,
      loop,
      observer,
      velocityTrigger: null,
      speedReset: null,
    } satisfies MarqueeEntry;

    entry.velocityTrigger = ScrollTrigger.create({
      trigger: marquee,
      start: 'top bottom',
      end: 'bottom top',
      onUpdate: (self) => {
        if (loop.paused()) return;
        const velocityBoost = 1 + Math.min(Math.abs(self.getVelocity()) / 2500, 0.8);
        loop.timeScale(velocityBoost);
        entry.speedReset?.kill();
        entry.speedReset = gsap.to(loop, {
          timeScale: 1,
          duration: 0.8,
          ease: 'power3.out',
          overwrite: true,
        });
      },
    });

    marqueeEntries.push(entry);
  });
}

function setupGeneric(container: HTMLElement): void {
  if (prefersReducedMotion()) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    container.querySelectorAll('[data-reveal], [data-reveal-stagger]').forEach((el) => io.observe(el));
    revealObservers.push({ container, io });
    return;
  }
  container.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
    gsap.fromTo(
      el,
      { y: 40, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 90%' },
        onComplete: () => el.classList.add('in'),
        overwrite: 'auto',
      }
    );
  });
  container.querySelectorAll<HTMLElement>('[data-reveal-stagger]').forEach((group) => {
    gsap.fromTo(
      group.children,
      { y: 36, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.09,
        scrollTrigger: { trigger: group, start: 'top 88%' },
        onComplete: () => group.classList.add('in'),
        overwrite: 'auto',
      }
    );
  });
  container.querySelectorAll<HTMLElement>('[data-parallax]').forEach((el) => {
    gsap.fromTo(
      el,
      { yPercent: -9 },
      {
        yPercent: 9,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  });
}

/* ---------------- SPLATTER ---------------- */
function buildSplatter(container: HTMLElement): void {
  if (prefersReducedMotion()) return;
  const palette = ['--o', '--o-light', '--wo-light', '--ember', '--grey', '--white', '--wo-soft', '--grey-light', '--o-dark'];
  container.querySelectorAll<HTMLElement>('[data-splatter]').forEach((c) => {
    if (c.dataset.splatDone === 'true') return;
    c.dataset.splatDone = 'true';
    const n = parseInt(c.dataset.splatter || '20', 10);
    const dots: SplatDot[] = [];
    for (let i = 0; i < n; i++) {
      const d = document.createElement('span');
      d.className = 'splat';
      const r = Math.random();
      const size = r * r * 15 + 2; // 2-17px
      const col = palette[(Math.random() * palette.length) | 0];
      const fx = Math.random();
      const fy = Math.random();
      d.style.width = d.style.height = size.toFixed(1) + 'px';
      d.style.left = (fx * 100).toFixed(2) + '%';
      d.style.top = (fy * 100).toFixed(2) + '%';
      const baseOp = +(0.1 + Math.random() * 0.45).toFixed(2);
      d.style.opacity = String(baseOp);
      if (Math.random() < 0.28) {
        d.classList.add('ring');
        d.style.border = '1px solid var(' + col + ')';
      } else {
        d.style.background = 'var(' + col + ')';
      }
      if (Math.random() < 0.1) d.classList.add('glow');
      c.appendChild(d);
      dots.push({ 
        el: d, 
        depth: 0.25 + Math.random() * 1.6, 
        baseOp, 
        phase: Math.random() * 6.283, 
        amp: 4 + Math.random() * 10, 
        ox: fx, oy: fy,
        currX: 0,
        currY: 0,
        currGlow: 0
      });
    }
    splatField.push({ container: c, dots, rect: toCachedRect(c.getBoundingClientRect()) });
  });
}

/* ---------------- ORBIT ---------------- */
function buildOrbits(container: HTMLElement): void {
  if (prefersReducedMotion()) return;
  const palette = ['--o', '--o-light', '--wo-light', '--ember', '--white', '--wo-soft', '--grey-light', '--o-dark'];
  container.querySelectorAll<HTMLElement>('[data-orbit], [data-small-circles]').forEach((c) => {
    if (c.dataset.orbitDone === 'true') return;
    c.dataset.orbitDone = 'true';

    const isSmall = c.hasAttribute('data-small-circles');
    const scrubDriven = c.hasAttribute('data-scrub-orbit');
    const rings: OrbitRing[] = [];
    const count = isSmall ? 5 + Math.floor(Math.random() * 3) : 8 + Math.floor(Math.random() * 5);

    // Desktop keeps the reference's oversampled ring treatment exactly. On a
    // touch-primary device the same 6.3x source geometry can create multiple
    // 2500-4000px composited textures for a single visible orbit, which is
    // enough to make mobile Safari terminate the tab while scrolling. Render
    // at the final visible size on touch devices; colour, border, position,
    // opacity and final scale remain visually identical.
    const REFERENCE_ENTRANCE_SCALE = 6.3;
    const scaleFactor = usesTouchPrimaryInput() ? 1 : REFERENCE_ENTRANCE_SCALE;
    for (let i = 0; i < count; i++) {
      const r = document.createElement('span');
      r.className = 'orbit-ring';
      
      const baseSize = isSmall 
        ? 150 + i * 40 + Math.random() * 20 
        : 120 + i * 45 + Math.random() * 25;
      
      const size = baseSize * scaleFactor;
      const col = palette[i % palette.length];

      const baseBorder = isSmall ? 1.0 : 0.5;
      const targetOp = isSmall 
        ? 0.15 + (1 - i / count) * 0.35 
        : 0.05 + (1 - i / count) * 0.25;

      Object.assign(r.style, {
        width: `${size}px`,
        height: `${size}px`,
        border: `${baseBorder * scaleFactor}px solid var(${col})`,
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0',
      });

      c.appendChild(r);
      const ringObj: OrbitRing = {
        el: r,
        depth: 0.4 + (i / count) * 1.4,
        baseOp: 0,
        targetOp,
        phase: Math.random() * Math.PI * 2,
        size,
        scaleFactor,
        // Keep the same apparent 6.3x-to-1x entrance on every device. Mobile
        // achieves it as a transform of the final-size texture instead of
        // allocating the texture itself at 6.3x resolution.
        entranceScale: REFERENCE_ENTRANCE_SCALE,
        currX: 0,
        currY: 0,
      };
      rings.push(ringObj);

      if (!scrubDriven) {
        gsap.to(ringObj, {
          baseOp: targetOp,
          entranceScale: 1,
          duration: 1.8,
          ease: 'power4.out',
          delay: i * 0.06,
        });
      }
    }
    orbitField.push({ container: c, rings, rect: toCachedRect(c.getBoundingClientRect()), scrubDriven });
  });
}

function setupSplatterLoop(): void {
  if (prefersReducedMotion()) return;
  let mx = -9999,
    my = -9999;
  window.addEventListener(
    'mousemove',
    (e) => {
      mx = e.clientX;
      my = e.clientY;
    },
    { passive: true }
  );

  // Runs on the shared GSAP ticker (one scheduler for all per-frame motion,
  // same clock Lenis/ScrollTrigger use) instead of a private, uncancellable
  // requestAnimationFrame chain. Early-outs when no fields exist on the page.
  let vel = 0;
  function loop(time: number) {
    if (!splatField.length && !orbitField.length) return;
    refreshFieldRectsIfNeeded();
    vel = window.__sv || 0;
    const t = time;
    for (const f of splatField) {
      const R = f.rect;
      if (!R || R.bottom < -100 || R.top > getStableViewportHeight() + 100) continue;
      for (const p of f.dots) {
        // Calculate Target
        let targetX = Math.sin(t * 0.6 + p.phase) * p.amp;
        let targetY = Math.cos(t * 0.5 + p.phase) * p.amp * 0.7;
        targetY += vel * p.depth * 0.6;

        const cx = R.left + p.ox * R.width;
        const cy = R.top + p.oy * R.height;
        const ddx = cx - mx,
          ddy = cy - my;
        const dist = Math.hypot(ddx, ddy);
        let glowTarget = 0;

        if (dist < 160) {
          const force = 1 - dist / 160;
          targetX += (ddx / (dist || 1)) * force * 34;
          targetY += (ddy / (dist || 1)) * force * 34;
          glowTarget = force;
        }

        // Apply Damping (LERP) for Spring-like feel
        p.currX += (targetX - p.currX) * 0.12;
        p.currY += (targetY - p.currY) * 0.12;
        p.currGlow += (glowTarget - p.currGlow) * 0.15;

        p.el.style.transform = `translate(${p.currX.toFixed(1)}px, ${p.currY.toFixed(1)}px) scale(${(1 + p.currGlow * 0.9).toFixed(2)})`;
        p.el.style.opacity = p.baseOp.toFixed(2);
      }
    }

    for (const f of orbitField) {
      const R = f.rect;
      if (!R || R.bottom < -200 || R.top > getStableViewportHeight() + 200) continue;

      for (const p of f.rings) {
        const floatX = Math.sin(t * 0.35 + p.phase) * 18;
        const floatY = Math.cos(t * 0.25 + p.phase) * 12;
        const pulse = 1 + Math.sin(t * 0.7 + p.phase) * 0.06;

        const cx = R.left + R.width / 2;
        const cy = R.top + R.height / 2;
        const ddx = cx - mx,
          ddy = cy - my;
        const dist = Math.hypot(ddx, ddy);

        let targetX = floatX;
        let targetY = floatY + vel * p.depth * 0.75;

        if (dist < 450) {
          const force = (1 - dist / 450) * 45 * p.depth;
          targetX += (ddx / (dist || 1)) * force;
          targetY += (ddy / (dist || 1)) * force;
        }

        // Apply Damping (LERP)
        p.currX += (targetX - p.currX) * 0.08;
        p.currY += (targetY - p.currY) * 0.08;

        p.el.style.transform = `translate(calc(-50% + ${p.currX.toFixed(1)}px), calc(-50% + ${p.currY.toFixed(1)}px)) scale(${((pulse * p.entranceScale) / p.scaleFactor).toFixed(3)})`;
        p.el.style.opacity = p.baseOp.toFixed(2);
      }
    }
  }
  gsap.ticker.add(loop);
}

/* ---------------- Scroll velocity tracker ---------------- */
let lastY = 0;
export function resetScrollMotionState(scrollY: number): void {
  // Route navigation jumps the document to the top. That programmatic jump is
  // not user velocity and must never feed the reactive orbit-ring Y offset.
  lastY = scrollY;
  window.__sv = 0;
  fieldRectsScrollY = scrollY;
  fieldRectsDirty = true;
}

function setupVelocityTracker(): void {
  lastY = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      window.__sv = window.scrollY - lastY;
      lastY = window.scrollY;
    },
    { passive: true }
  );
}

function refreshFieldRectsIfNeeded(): void {
  if (typeof window === 'undefined') return;
  const sy = window.scrollY;
  const dy = sy - fieldRectsScrollY;

  if (fieldRectsDirty) {
    // Real reflow — only on resize/init/refresh
    splatField.forEach((f) => {
      const r = f.container.getBoundingClientRect();
      f.rect = { top: r.top, bottom: r.bottom, left: r.left, width: r.width, height: r.height };
    });
    orbitField.forEach((f) => {
      const r = f.container.getBoundingClientRect();
      f.rect = { top: r.top, bottom: r.bottom, left: r.left, width: r.width, height: r.height };
    });
    fieldRectsDirty = false;
  } else if (dy !== 0) {
    // Delta-based update — NO reflow. Immutable replacement to avoid read-only errors.
    splatField.forEach((f) => {
      if (f.rect) {
        f.rect = { ...f.rect, top: f.rect.top - dy, bottom: f.rect.bottom - dy };
      }
    });
    orbitField.forEach((f) => {
      if (f.rect) {
        f.rect = { ...f.rect, top: f.rect.top - dy, bottom: f.rect.bottom - dy };
      }
    });
  }
  fieldRectsScrollY = sy;
}

/* ---------------- Cached document height ----------------
   scrollHeight forces layout when read; reading it on every scroll event
   under Lenis means a forced reflow per frame. The height only changes on
   resize / page swap / accordion toggles — all of which end in a
   ScrollTrigger refresh — so cache it and refresh on those signals only. */
let docScrollHeight = 0;
function refreshDocHeight(): void {
  docScrollHeight = document.documentElement.scrollHeight;
}

/* ---------------- Header scroll state ---------------- */
function setupHeaderScroll(): void {
  const header = document.querySelector('.site-header');
  const progress = document.querySelector<HTMLElement>('.scroll-progress');
  refreshDocHeight();
  function onScroll() {
    const y = window.scrollY || window.pageYOffset;
    if (header) header.classList.toggle('scrolled', y > 30);
    if (progress) {
      const h = docScrollHeight - getStableViewportHeight();
      // scaleX (compositor-only) instead of width (layout per frame).
      progress.style.transform = 'scaleX(' + (h > 0 ? Math.min(y / h, 1) : 0) + ')';
    }
    updateScrollbar();
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ---------------- Custom overlay scrollbar (cross-browser) ---------------- */
function setupCustomScrollbar(): void {
  if (!canHover()) return; // desktop/pointer devices only
  const bar = document.createElement('div');
  bar.className = 'custom-scrollbar';
  const thumb = document.createElement('div');
  thumb.className = 'custom-scrollbar__thumb';
  bar.appendChild(thumb);
  document.body.appendChild(bar);

  let dragging = false,
    startY = 0,
    startScroll = 0;

  updateScrollbar = function () {
    const winH = getStableViewportHeight();
    const scrollH = docScrollHeight || document.documentElement.scrollHeight;
    const maxScroll = scrollH - winH;
    if (maxScroll <= 1) {
      bar.classList.remove('is-visible');
      return;
    }
    bar.classList.add('is-visible');
    const thumbH = Math.max((winH / scrollH) * winH, 40);
    const y = window.scrollY || window.pageYOffset;
    const maxThumbY = winH - thumbH;
    thumb.style.height = thumbH + 'px';
    thumb.style.transform = 'translateY(' + Math.min(y / maxScroll, 1) * maxThumbY + 'px)';
  };

  thumb.addEventListener('pointerdown', (e) => {
    dragging = true;
    startY = e.clientY;
    startScroll = window.scrollY || window.pageYOffset;
    thumb.classList.add('is-dragging');
    thumb.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  window.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const winH = getStableViewportHeight();
    const maxScroll = document.documentElement.scrollHeight - winH;
    const maxThumbY = winH - thumb.offsetHeight;
    if (maxThumbY <= 0) return;
    let target = startScroll + (e.clientY - startY) * (maxScroll / maxThumbY);
    target = Math.max(0, Math.min(maxScroll, target));
    if (lenis) lenis.scrollTo(target, { immediate: true });
    else window.scrollTo(0, target);
  });
  window.addEventListener('pointerup', () => {
    if (!dragging) return;
    dragging = false;
    thumb.classList.remove('is-dragging');
  });

  window.addEventListener('resize', updateScrollbar, { passive: true });
  if (lenis && typeof lenis.on === 'function') lenis.on('scroll', updateScrollbar);
  updateScrollbar();
}

/* ---------------- Custom cursor ---------------- */
function initCustomCursor(): void {
  if (!canHover() || prefersReducedMotion()) return;
  const dot = document.createElement('div');
  dot.className = 'cursor-dot';
  const cursorRing = document.createElement('div');
  cursorRing.className = 'cursor-ring';
  document.body.append(dot, cursorRing);
  let mx = -100,
    my = -100,
    rx = -100,
    ry = -100;
  window.addEventListener('mousemove', (e) => {
    mx = e.clientX;
    my = e.clientY;
    dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%,-50%)`;
  });
  // Ring lerp on the shared GSAP ticker — avoids a second private rAF chain.
  // Skips the style write once the ring has settled on the target (<0.1px).
  gsap.ticker.add(() => {
    const ddx = mx - rx;
    const ddy = my - ry;
    if (Math.abs(ddx) < 0.1 && Math.abs(ddy) < 0.1) return;
    rx += ddx * 0.18;
    ry += ddy * 0.18;
    cursorRing.style.transform = `translate(${rx}px, ${ry}px) translate(-50%,-50%)`;
  });

  document.body.addEventListener('mouseover', (e) => {
    const el = (e.target as HTMLElement).closest(
      'a, button, .work-item, .case-card, .step, .news-row, .h-step, .faq-q, .filter-btn'
    );
    if (el && cursorRing) cursorRing.classList.add('hover');
  });
  document.body.addEventListener('mouseout', (e) => {
    const el = (e.target as HTMLElement).closest(
      'a, button, .work-item, .case-card, .step, .news-row, .h-step, .faq-q, .filter-btn'
    );
    if (el && cursorRing) cursorRing.classList.remove('hover');
  });
}

/* ---------------- Magnetic buttons ---------------- */
function setupMagneticButtons(container: HTMLElement): void {
  if (!canHover() || prefersReducedMotion()) return;
  container.querySelectorAll<HTMLElement>('.btn').forEach((btn) => {
    if (btn.dataset.magnetic === 'true') return;
    btn.dataset.magnetic = 'true';

    let r: DOMRect | null = null;
    const xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'osmo' });
    const yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'osmo' });
    const sTo = gsap.quickTo(btn, 'scale', { duration: 0.3, ease: 'osmo' });

    btn.addEventListener('mouseenter', () => {
      r = btn.getBoundingClientRect();
      sTo(1.05);
    });

    btn.addEventListener('mousemove', (e) => {
      if (!r) return;
      const x = (e.clientX - r.left - r.width / 2) * 0.28;
      const y = (e.clientY - r.top - r.height / 2) * 0.38;
      xTo(x);
      yTo(y);
    });
    btn.addEventListener('mousedown', () => sTo(0.96));
    btn.addEventListener('mouseup', () => sTo(1.05));
    btn.addEventListener('mouseleave', () => {
      r = null;
      xTo(0);
      yTo(0);
      sTo(1);
    });
  });
}

/* ---------------- Footer weather (Open-Meteo, no API key) ---------------- */
const WEATHER_ICONS: Record<string, string> = {
  clear:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>',
  partly:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="8.3" cy="8.3" r="3.2"/><path d="M8.3 2.9v1.5M13 8.3h1.5M4.3 5.3l1.1 1.1"/><path d="M6.2 21h11.3A3.75 3.75 0 0 0 18 13.53 5 5 0 0 0 8.7 12.2 4 4 0 0 0 6.2 21Z"/></svg>',
  cloudy:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 19h11a3.75 3.75 0 0 0 .5-7.47A5.5 5.5 0 0 0 7.2 11.7 4 4 0 0 0 6.5 19Z"/></svg>',
  fog: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M4 9h16M2 13h20M4 17h16"/></svg>',
  rain: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 15h11a3.75 3.75 0 0 0 .5-7.47A5.5 5.5 0 0 0 7.2 7.7 4 4 0 0 0 6.5 15Z"/><path d="M8 18.5 7 21M12 18.5l-1 2.5M16 18.5l-1 2.5"/></svg>',
  snow: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 15h11a3.75 3.75 0 0 0 .5-7.47A5.5 5.5 0 0 0 7.2 7.7 4 4 0 0 0 6.5 15Z"/><path d="M8 18v3M12 18v3M16 18v3M7 19.5l2-1M11 19.5l2-1M15 19.5l2-1"/></svg>',
  storm:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 13h11a3.75 3.75 0 0 0 .5-7.47A5.5 5.5 0 0 0 7.2 5.7 4 4 0 0 0 6.5 13Z"/><path d="M13 13l-3 5h3l-2 4"/></svg>',
};
function weatherIconFor(code: number): string {
  if (code === 0) return WEATHER_ICONS.clear;
  if (code >= 1 && code <= 3) return WEATHER_ICONS.partly;
  if (code === 45 || code === 48) return WEATHER_ICONS.fog;
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return WEATHER_ICONS.rain;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return WEATHER_ICONS.snow;
  if (code >= 95) return WEATHER_ICONS.storm;
  return WEATHER_ICONS.cloudy;
}
function setupFooterWeather(): void {
  // Consent gate: the weather is server-proxied (no visitor data reaches
  // Open-Meteo), but the cookie dialog exposes it as a functional service —
  // respect an explicit opt-out.
  try {
    const raw = window.localStorage.getItem('rh-cookie-consent');
    if (raw) {
      const consent = JSON.parse(raw);
      if (consent?.services?.weather === false) {
        document
          .querySelectorAll<HTMLElement>('.footer-weather')
          .forEach((el) => (el.style.display = 'none'));
        return;
      }
    }
  } catch {
    /* unreadable consent state — treat as default (allowed) */
  }
  document.querySelectorAll<HTMLElement>('.footer-loc[data-weather-lat]').forEach(async (loc) => {
    const iconEl = loc.querySelector<HTMLElement>('.w-icon');
    const tempEl = loc.querySelector<HTMLElement>('.w-temp');
    if (!iconEl || !tempEl) return;
    const lat = loc.dataset.weatherLat,
      lon = loc.dataset.weatherLon;
    try {
      // Same-origin proxy (see app/api/weather) — DSGVO: no visitor IP ever
      // reaches Open-Meteo; the server fetches + caches the data itself.
      const weatherUrl = window.RH_THEME?.weatherUrl || '/wp-json/rh/v1/weather';
      const separator = weatherUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${weatherUrl}${separator}lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`);
      if (!res.ok) throw new Error('weather request failed');
      const data = await res.json();
      if (typeof data.temperature !== 'number') return;
      iconEl.innerHTML = weatherIconFor(data.weathercode);
      tempEl.textContent = Math.round(data.temperature) + '°C';
    } catch (e) {
      const w = loc.querySelector<HTMLElement>('.footer-weather');
      if (w) w.style.display = 'none';
    }
  });
}

/* ---------------- Anchor clicks intercept for Lenis ---------------- */
function setupAnchorScrolls(container: HTMLElement, onNavigate?: () => void): void {
  container.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => {
    if (a.dataset.anchorBound === 'true') return;
    a.dataset.anchorBound = 'true';
    a.addEventListener('click', (e) => {
      const id = a.getAttribute('href');
      if (!id || id.length < 2) return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      if (lenis) lenis.scrollTo(el as HTMLElement, { offset: -70, duration: 1.4 });
      else (el as HTMLElement).scrollIntoView({ behavior: 'smooth' });
      onNavigate?.();
    });
  });
}

/* ---------------- FAQ accordion ---------------- */
function setupFaqAccordion(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('.faq-q').forEach((q) => {
    if (q.dataset.faqBound === 'true') return;
    q.dataset.faqBound = 'true';
    q.addEventListener('click', () => {
      const item = q.closest('.faq-item');
      if (!item) return;
      const ans = item.querySelector<HTMLElement>('.faq-a');
      const open = item.classList.toggle('open');
      
      if (ans) {
        gsap.to(ans, {
          maxHeight: open ? ans.scrollHeight : 0,
          opacity: open ? 1 : 0,
          duration: 0.4,
          ease: 'osmo',
          onComplete: refreshScrollTrigger
        });
      } else {
        refreshScrollTrigger();
      }
    });
  });
}

/* ---------------- Cases filter ---------------- */
function setupCasesFilter(container: HTMLElement): void {
  const filterBar = container.querySelector<HTMLElement>('.filter-bar');
  if (!filterBar) return;
  if (filterBar.dataset.filterBound === 'true') return;
  filterBar.dataset.filterBound = 'true';
  const cards = container.querySelectorAll<HTMLElement>('.case-card');
  filterBar.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.filter-btn');
    if (!btn) return;
    filterBar.querySelectorAll('.filter-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    const f = btn.dataset.filter;
    
    // Coordinated Filter Reveal
    gsap.to(cards, {
      autoAlpha: 0,
      scale: 0.95,
      duration: 0.2,
      ease: 'power2.in',
      onComplete: () => {
        cards.forEach((c) => {
          const tags = (c.dataset.tags || '').split(',');
          c.classList.toggle('hide', !(f === 'all' || (f !== undefined && tags.includes(f))));
        });
        
        const visibleCards = Array.from(cards).filter(c => !c.classList.contains('hide'));
        gsap.fromTo(visibleCards, 
          { autoAlpha: 0, scale: 0.95, y: 10 },
          { 
            autoAlpha: 1, 
            scale: 1, 
            y: 0, 
            duration: 0.4, 
            stagger: 0.05, 
            ease: 'osmo',
            clearProps: 'all'
          }
        );
        refreshScrollTrigger();
      }
    });
  });
}

/* ============================================================
   LIFECYCLE
   ============================================================ */

// Runs once for the lifespan of the app (mirrors Barba `initOnce`).
// Wires the persistent chrome (header, footer, cursor, scrollbar).
export function initOnce(): void {
  if (onceInitialised || typeof window === 'undefined') return;
  onceInitialised = true;

  // Register plugins once
  gsap.registerPlugin(ScrollTrigger, CustomEase);

  // Enterprise Configuration
  ScrollTrigger.config({
    limitCallbacks: true, // prevent flooding during fast scrolls
    ignoreMobileResize: true, // prevent jumps on address bar hide/show
  });

  if (prefersReducedMotion()) document.documentElement.classList.add('no-gsap');

  initLenis();
  setupVelocityTracker();

  CustomEase.create('osmo', '0.625, 0.05, 0, 1');
  gsap.defaults({ ease: 'osmo', duration: 0.6 });

  setupHeaderScroll();
  setupViewportResizeHandling();
  setupCustomScrollbar();
  initCustomCursor();
  setupSplatterLoop();
  setupFooterWeather();

  // Initial refresh after a small delay for fonts/layout
  setTimeout(() => {
    ScrollTrigger.refresh();
  }, 200);

  // Keep the cached document height in sync (resize + every ScrollTrigger
  // refresh — page swaps, accordion toggles etc. all end in a refresh).
  ScrollTrigger.addEventListener('refresh', refreshDocHeight);

  const headerEl = document.querySelector<HTMLElement>('.site-header');
  if (headerEl) setupMagneticButtons(headerEl);
}

// Runs for every route's <main> container (mirrors Barba `afterEnter`).
export function initPage(container: HTMLElement | null, closeNav: () => void): void {
  if (!container) return;
  if (initializedContainers.has(container)) return;
  initializedContainers.add(container);

  // View-based logic: find if we have a view registered for this namespace
  const ns = container.dataset.barbaNamespace;
  const view = registeredViews.find((v) => v.namespace === ns);

  // Revert any previous page context before creating a new one
  if (pageContext) pageContext.revert();
  pageContext = null;
  pageContextContainer = null;
  pageContext = gsap.context(() => {
    if (view?.beforeEnter) view.beforeEnter(container);

    const yr = container.querySelector<HTMLElement>('[data-year]');
    if (yr) yr.textContent = String(new Date().getFullYear());

    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        refreshScrollTrigger();
      });
    }

    setupWordReveals(container);
    // Build orbit entries before the scrub timelines so a scrub CTA can add
    // its rings to the same ScrollTrigger-controlled timeline as its words.
    buildOrbits(container);

    if (!prefersReducedMotion()) {
      container.querySelectorAll<HTMLElement>('[data-scrub], [data-horizontal]').forEach((el) => {
        if (el.matches('[data-horizontal]')) setupHorizontalOne(el);
        else setupScrub(el);
      });
    }

    container.querySelectorAll<HTMLElement>('[data-rh-backstory-ready="true"]').forEach(setupBackstory);
    setupGeneric(container);
    setupMarquees(container);
    buildSplatter(container);
    setupMagneticButtons(container);
    setupFaqAccordion(container);
    setupCasesFilter(container);
    setupAnchorScrolls(container, closeNav);

    if (view?.afterEnter) view.afterEnter(container);
  }, container);
  pageContextContainer = container;

  refreshScrollTrigger();
}

// Clean up per-page side effects (mirrors Barba `beforeLeave` / `afterLeave`).
export function cleanupPage(container: HTMLElement | null): void {
  if (!container || container.querySelector('[data-rh-backstory-ready="true"]')) {
    document.body.classList.remove('rh-backstory-guide-active');
  }
  if (!container) {
    if (pageContext) {
      pageContext.revert();
      pageContext = null;
      pageContextContainer = null;
    }
    revealObservers.forEach((o) => o.io.disconnect());
    revealObservers.length = 0;
    cleanupMarquees(null);
    splatField.length = 0;
    orbitField.length = 0;
    return;
  }

  const ns = container.dataset.barbaNamespace;
  const view = registeredViews.find((v) => v.namespace === ns);
  if (view?.beforeLeave) view.beforeLeave(container);

  // A route cleanup can arrive after another page has already mounted. Never
  // let a stale container revert the current page's GSAP context.
  if (pageContext && pageContextContainer === container) {
    pageContext.revert();
    pageContext = null;
    pageContextContainer = null;
  }

  // Element-specific field cleanup
  for (let i = splatField.length - 1; i >= 0; i--) {
    if (container.contains(splatField[i].container)) {
      const c = splatField[i].container;
      c.querySelectorAll('.splat').forEach((d) => d.remove());
      delete c.dataset.splatDone;
      splatField.splice(i, 1);
    }
  }
  for (let i = orbitField.length - 1; i >= 0; i--) {
    if (container.contains(orbitField[i].container)) {
      const c = orbitField[i].container;
      c.querySelectorAll('.orbit-ring').forEach((r) => r.remove());
      delete c.dataset.orbitDone;
      orbitField.splice(i, 1);
    }
  }
  for (let i = revealObservers.length - 1; i >= 0; i--) {
    if (container.contains(revealObservers[i].container) || revealObservers[i].container === container) {
      revealObservers[i].io.disconnect();
      revealObservers.splice(i, 1);
    }
  }
  cleanupMarquees(container);
  initializedContainers.delete(container);
}
